import { useMachine } from '@xstate/compiled/react';
import { ReactComponent as HomeIcon } from 'assets/icons/icon-monitor.svg';
import { JcapReactSelect } from 'components/JcapReactSelect/JcapReactSelect';
import Page from 'components/Page';
import { useHasura } from 'context/ApiState';
import { useSearchBusinessUnitsQuery } from 'graphql/queries/SearchBusinessUnits.generated';
import { useSearchCurrencyBooksQuery } from 'graphql/queries/SearchCurrencyBooks.generated';
import React, { ChangeEvent, Suspense } from 'react';
import ChartBalanceHistory from './ChartBalanceHistory/ChartBalanceHistory';
import ChartBankExposure from './ChartBankExposure/ChartBankExposure';
import ChartLiquidity from './ChartLiquidity/ChartLiquidity';
import { dashboardMachine } from './dashboardFilters.machine';
import useDashboardData from './hooks/useDashboardData';
import SummaryBar from './SummaryBar/SummaryBar';
import TableCurrencyBooks from './TableCurrencyBooks/TableCurrencyBooks';
import { Order_By } from 'graphql/schemaTypes.generated';
import Bugsnag from '@bugsnag/js';
import DemoStaticDashboard from 'components/DemoStaticDashboard/DemoStaticDashboard';
import { useFetchConversionRates } from 'toolbox';
import { ConvertFunction } from 'toolbox/src/hooks/useFetchConversionRates';
import Checkbox from 'components/Checkbox';
import ReactTooltip from 'react-tooltip';

const Dashboard = () => {
  const { convert, state, data } = useFetchConversionRates(
    process.env.REACT_APP_EXCHANGERATE_API_KEY,
  );

  if (state === 'error') console.warn('conversion error');

  if (state === 'success') {
    return (
      <Suspense fallback={null}>
        <DashboardInner convert={convert} conversionData={data} />
      </Suspense>
    );
  }
  return null;
};

const DashboardInner = ({
  convert,
  conversionData,
}: {
  convert: ConvertFunction;
  conversionData;
}) => {
  const { app_user } = useHasura();

  const [state, send] = useMachine(dashboardMachine, {});

  let currencyBookId = undefined;

  if (state.matches('filters.currencyBookSelected')) {
    currencyBookId = state.context.currencyBookId;
  } else if (state.matches('allCurrencyBookDefault.all')) {
    currencyBookId = 'all';
  }

  const { refetch, fetching, loading, ...data } = useDashboardData({
    organisation_id: app_user?.selected_organisation_id || 1,
    ...(!state.matches('filters.currencyBookSelected') && {
      currency_book_filter: [],
    }),
    currency_book_id: currencyBookId,
    ...(state.matches('filters.businessUnitSelected') && {
      business_unit_id: state.context.businessUnitId,
    }),
    convert,
    shouldUseModelBalances: state.matches('modelBalances.showing'),
  });

  const [businessUnitsResult] = useSearchBusinessUnitsQuery({
    variables: {
      where: {
        organisation_id: { _eq: app_user?.selected_organisation_id },
      },
      order_by: [
        {
          name: Order_By.Asc,
        },
      ],
    },
  });
  if (businessUnitsResult.error) Bugsnag.notify(businessUnitsResult.error);

  const [currencyBooksResult] = useSearchCurrencyBooksQuery({
    variables: {
      where: state.context.businessUnitId
        ? {
            business_unit_id: { _eq: state.context.businessUnitId },
          }
        : {
            business_unit: {
              organisation_id: { _eq: app_user?.selected_organisation_id },
            },
          },
      order_by: [
        {
          name: Order_By.Asc,
        },
      ],
    },
  });
  if (currencyBooksResult.error) Bugsnag.notify(currencyBooksResult.error);

  const hour = new Date().getHours();
  const greeting =
    'Good ' +
    ((hour < 12 && 'Morning') || (hour < 18 && 'Afternoon') || 'Evening');

  const chooseWhetherToShowModelBalances = (shouldShow: boolean) => {
    if (shouldShow) {
      send('CHOOSE_SHOW_MODEL_BALANCES');
    } else {
      send('CHOOSE_HIDE_MODEL_BALANCES');
    }
  };

  const handleChangeBusinessUnitFilter = (
    e: ChangeEvent<HTMLSelectElement>,
  ) => {
    if (e.target.value) {
      send({
        type: 'CHOOSE_BUSINESS_UNIT',
        id: Number(e.target.value),
      });
    } else {
      send({ type: 'CHOOSE_ALL_BUSINESS_UNITS' });
    }
  };

  const handleChangeCurrencyBookFilter = (
    e: ChangeEvent<HTMLSelectElement>,
  ) => {
    if (!e.target.value) {
      send({
        type: 'CHOOSE_GBP_EUR_USD_CURRENCY_BOOKS',
      });
    } else if (e.target.value === 'all') {
      send({
        type: 'CHOOSE_ALL_CURRENCY_BOOKS',
      });
    } else {
      send({
        type: 'CHOOSE_CURRENCY_BOOK',
        id: e.target.value,
      });
    }
  };

  const pageProps = {
    titleRight: (
      <div className="flex items-end -mb-4">
        <JcapReactSelect
          options={[
            { id: undefined, name: 'All' },
            ...(businessUnitsResult.data?.business_units || []),
          ]}
          className="w-48 mr-2"
          value={state.context.businessUnitId}
          valueAccessor={({ id }) => id}
          onChange={handleChangeBusinessUnitFilter}
          labelAccessor={({ name }) => name}
          label="Business Unit"
        />
        <JcapReactSelect
          options={[
            { id: undefined, name: 'GBP, EUR, USD only' },
            { id: 'all', name: 'All Currencies' },
            ...(currencyBooksResult.data?.currency_books || []),
          ]}
          className="w-64 mr-2"
          value={currencyBookId}
          valueAccessor={({ id }) => id}
          onChange={handleChangeCurrencyBookFilter}
          labelAccessor={({ name }) => name}
          label="Currency Book"
        />
        <JcapReactSelect
          className="w-48"
          label="Base Currency"
          onChange={(e) => {
            send({
              type: 'UPDATE_CURRENCY',
              currency: e.target.value as any,
            });
          }}
          value={state.context.currency}
          valueAccessor={({ value }) => value}
          labelAccessor={({ label }) => label}
          options={[
            {
              value: 'GBP',
              label: 'GBP',
            },
            {
              value: 'USD',
              label: 'USD',
            },
            {
              value: 'EUR',
              label: 'EUR',
            },
          ]}
        />
      </div>
    ),
    icon: (
      <HomeIcon className="inline w-10 h-10 mr-2 -mb-1 -ml-2 text-gray-500" />
    ),
    title: `${greeting} ${app_user?.name?.split(' ')[0] || ''}`,
  };

  if (!app_user) {
    return null;
  }

  if (
    app_user?.selected_organisation?.demo_dashboard === undefined ||
    app_user?.selected_organisation?.demo_dashboard === true
  )
    return <DemoStaticDashboard />;

  if (data.aggr_balance_history.length < 1) {
    return (
      <Page {...pageProps}>
        {state.matches('filters.nothingSelected') && (
          <p>
            This organisation does not yet have any balance data to display.
          </p>
        )}
        {state.matches('filters.businessUnitSelected') && (
          <p>
            This business unit does not yet have any balance data to display.
          </p>
        )}
        {state.matches('filters.currencyBookSelected') && (
          <p>
            This currency book does not yet have any balance data to display.
          </p>
        )}
      </Page>
    );
  }

  const dataFilter = {
    GBP: {
      latest: data.gbpLatest,
      previous: data.gbpPrevious,
    },
    USD: {
      latest: data.usdLatest,
      previous: data.usdPrevious,
    },
    EUR: {
      latest: data.eurLatest,
      previous: data.eurPrevious,
    },
  };

  const latestData = dataFilter[state.context.currency]?.latest;

  return (
    <Page {...pageProps}>
      {conversionData?.success !== true ? (
        <div className="fixed top-0 left-0 z-50 block w-full px-8 py-6 bg-red-600 hover:bg-red-700">
          <p className="font-semibold text-white">
            Error fetching Exchange Rate data
          </p>
        </div>
      ) : undefined}

      <SummaryBar
        totalPosition={latestData.aggr_balance}
        totalPositionChange={latestData.aggr_balance_change}
        totalPositionChangePct={latestData?.aggr_balance_change_pct}
        annualYield={latestData.aggr_yield}
        averageRate={(latestData.aggr_yield / latestData.aggr_balance) * 100}
        changeRate={latestData.aggr_yield_change_pct}
        changeYield={latestData.aggr_yield_change}
        latestBalanceDate={latestData.date}
        currency={state.context.currency}
        isShowingModelBalances={state.matches('modelBalances.showing')}
      />

      <div className="flex flex-row items-start">
        <div className="flex-grow">
          <TableCurrencyBooks
            currencyBookSelectChoice={currencyBookId}
            changeCurrencyBookSelectChoice={handleChangeCurrencyBookFilter}
            currencyBookSelectOptions={[
              { id: undefined, name: 'GBP, EUR, USD only' },
              { id: 'all', name: 'All Currencies' },
              ...(currencyBooksResult.data?.currency_books || []),
            ]}
            businessUnitSelectChoice={state.context.businessUnitId}
            changeBusinessUnitSelectChoice={handleChangeBusinessUnitFilter}
            businessUnitSelectOptions={[
              { id: undefined, name: 'ALL' },
              ...(businessUnitsResult.data?.business_units || []),
            ]}
            convert={convert}
            currency_books={data.currency_books}
            isLimitingCurrencyBooks={state.matches(
              'filters.currencyBookSelected',
            )}
            currency={state.context.currency}
            reportModelBalanceChanged={() => refetch()}
            shouldUseModelBalances={state.matches('modelBalances.showing')}
          />
        </div>

        <div className="flex-shrink-0 w-5 h-5" />
        <div className="flex flex-col">
          <div
            className="flex items-center pb-4"
            data-tip="By default, charts on this dashboard are based upon real balances. Checking this option will render charts using your model balance data instead.
            Model balances can be edited by expanding rows in the Currency Book table to the left, and setting a Proposed Change for each account."
          >
            <Checkbox
              checked={state.matches('modelBalances.showing')}
              onChange={(e) => {
                chooseWhetherToShowModelBalances(e.target.checked);
              }}
              label="Render charts using Model Balances"
              className="w-8 h-8 fill-current text-jcap-500"
            />
            <ReactTooltip
              effect="solid"
              backgroundColor="black"
              className="max-w-xs"
              place="bottom"
            />
          </div>
          <ChartLiquidity
            data={data.liquidity_brackets}
            currency={state.context.currency}
          />
        </div>
      </div>

      <div>
        <ChartBankExposure
          aggr_balance={latestData.aggr_balance}
          banks={data.banks.sort((a, b) => (a.name > b.name ? 1 : -1))}
          currency={state.context.currency}
          bank_accounts={data.bank_accounts}
        />
      </div>

      <ChartBalanceHistory
        data={data.aggr_balance_history}
        currency={state.context.currency}
      />
    </Page>
  );
};

export default Dashboard;
